// Client for the combinatorial espelho engine. Thin wrappers over the
// server-side rules (POST /api/espelho/resolve) + the generator-backed preview
// image. Keeping the matching/availability logic server-side matches the
// "server-side rules" convention — the client only renders the result.

import type { EspelhoColor, Espelho, SlotFunction } from './espelhoCatalog';

// The functions a customer may place in a slot (mirrors the server's
// SELECTABLE_FUNCTIONS). `outro` is catalog-only and never offered here.
export const SELECTABLE_FUNCTIONS: SlotFunction[] = [
  'botoes-1', 'botoes-2', 'botoes-3', 'regulador', 'estores', 'cortinas', 'moldura',
];

export type Availability = 'em-catalogo' | 'encomenda-especial' | 'invalido';
export type NearestKind = 'recolor' | 'aproximacao';

export interface NearestMatch {
  espelho: Espelho;
  distance: number;
  kind: NearestKind;
  reasons: string[];
}

export interface ResolveResult {
  requested: {
    sku: string;
    size: number;
    slotFunctions: SlotFunction[];
    color: EspelhoColor;
    valid: boolean;
  };
  exact: Espelho | null;
  availability: Availability;
  previewSku: string;
  nearest: NearestMatch[];
}

export async function resolveCombo(
  size: number,
  slotFunctions: SlotFunction[],
  color: EspelhoColor,
  nearestLimit = 2,
): Promise<ResolveResult> {
  const res = await fetch('/api/espelho/resolve', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ size, slotFunctions, color, nearestLimit }),
  });
  if (!res.ok) throw new Error(`resolve ${res.status}`);
  const json = (await res.json()) as { success: boolean; data: ResolveResult };
  if (!json.success) throw new Error('resolve error');
  return json.data;
}

// URL of the generator-backed preview image. view '1' = clean thumbnail.
export function previewUrl(sku: string, view: '1' | '2' | '3' = '1'): string {
  return `/api/espelho/preview/${encodeURIComponent(sku)}.jpg?view=${view}`;
}

let previewStatusCache: boolean | null = null;
export async function previewAvailable(): Promise<boolean> {
  if (previewStatusCache !== null) return previewStatusCache;
  try {
    const res = await fetch('/api/espelho/preview-status');
    const json = (await res.json()) as { success: boolean; data: { available: boolean } };
    previewStatusCache = !!json.data?.available;
  } catch {
    previewStatusCache = false;
  }
  return previewStatusCache;
}
